import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { t, tn } from '@app/i18n';
import { dataHour } from '@app/format';
import { fieldMessage } from '@app/errors';
import type { ApiError } from '@app/api/ApiError';
import { Alert, Button, Card, ErrorBanner, LoadingButton, PageHeader, useToast } from '@zarya/ui';
import { useSites } from '../Sites.api';
import {
  useApplyAdmission,
  usePreviewAdmission,
  useRefreshAdmission,
  useRetryAdmission,
  useSiteAdmission,
} from './SiteAdmission.api';
import {
  cloneSettings,
  formPath,
  shortFingerprint,
  type AdmissionPreview,
  type AdmissionSettings,
  type AdmissionVersion,
  type SiteAdmission,
} from './SiteAdmissionModel';
import { useAdmissionValidation } from './useAdmissionValidation';
import { AdmissionChip } from './AdmissionChip';
import { AdmissionRegistration } from './AdmissionRegistration';
import { AdmissionSettingsForm } from './AdmissionSettingsForm';
import { AdmissionApplyDialog } from './AdmissionApplyDialog';
import { AdmissionHistoryDialog } from './AdmissionHistoryDialog';
import s from './SiteAdmission.module.css';

/**
 * «Sites › <site> › Admissão no cluster» (aim-xsk.8, protótipo §2). Página inteira, e não o drawer do Site, porque a
 * configuração é longa e cada mudança afeta todos os pods de agente do cluster. O endereço é pelo SLUG
 * (`/sites/<slug>/admissao`), que é o que a pessoa lê; as chamadas são pelo id, resolvido pela lista de Sites.
 */
export function SiteAdmissionPage() {
  const { slug = '' } = useParams<{ slug: string }>();
  const sites = useSites();
  const site = sites.data?.items.find((item) => item.slug === slug);
  const page = useSiteAdmission(site?.id);

  const breadcrumbs = [{ label: t('sites.list.title'), href: '/sites' }, { label: site?.name ?? slug }];

  if (sites.data && !site) {
    return (
      <>
        <PageHeader title={t('sites.admission.title')} breadcrumbs={breadcrumbs} icon="shield" />
        <Alert tone="warning" title={t('sites.admission.notFound.title')}>
          {t('sites.admission.notFound.message', { slug })} <Link to="/sites">{t('sites.admission.notFound.back')}</Link>
        </Alert>
      </>
    );
  }

  return (
    <>
      <PageHeader title={t('sites.admission.title')} subtitle={t('sites.admission.subtitle')} breadcrumbs={breadcrumbs} icon="shield" />
      {page.error ? <ErrorBanner error={page.error} title={t('sites.admission.error.load')} hideLink /> : null}
      {page.isPending || !site ? <p className={s.note}>{t('sites.admission.loading')}</p> : null}
      {page.data && site ? <Loaded key={site.id} siteId={site.id} page={page.data} /> : null}
    </>
  );
}

function Loaded({ siteId, page }: { siteId: string; page: SiteAdmission }) {
  const toast = useToast();
  const [reregister, setReregister] = useState(false);
  const [history, setHistory] = useState(false);
  const [preview, setPreview] = useState<{ result: AdmissionPreview; settings: AdmissionSettings } | null>(null);
  const [trigger, setTrigger] = useState<'manual' | 'restore'>('manual');

  const previewing = usePreviewAdmission(siteId);
  const apply = useApplyAdmission(siteId);
  const retry = useRetryAdmission(siteId);
  const refresh = useRefreshAdmission(siteId);

  const saved = useMemo(() => cloneSettings(page.settings), [page.settings]);
  const { control, handleSubmit, reset, setValue, setError, formState } = useForm<AdmissionSettings>({
    defaultValues: saved,
    ...useAdmissionValidation(),
  });

  // A versão salva mudou (aplicação, envio a cada aprovação, outra aba): o formulário volta a ela, a não ser que a
  // pessoa esteja no meio de uma edição — aí o «Revisar e aplicar» é que avisa, com `versao_antiga`.
  useEffect(() => {
    if (!formState.isDirty) reset(saved);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saved]);

  if (!page.registration.registered) {
    return (
      <>
        <Status page={page} />
        <Card title={t('sites.admission.register.title')}>
          <p>{t('sites.admission.register.intro')}</p>
          <AdmissionRegistration siteId={siteId} registration={page.registration} mode="first" onRegistered={() => toast.mostrar(t('sites.admission.toast.registered'), { tone: 'sucesso' })} />
        </Card>
      </>
    );
  }

  function serverFields(error: ApiError): boolean {
    let mapped = false;
    for (const campo of error.campos) {
      const path = formPath(campo.field);
      if (path) {
        setError(path as keyof AdmissionSettings, { type: 'server', message: fieldMessage(campo) });
        mapped = true;
      }
    }
    return mapped;
  }

  const review = handleSubmit((values) => {
    previewing.mutate(values, {
      onSuccess: (result) => setPreview({ result, settings: values }),
      onError: (error) => void serverFields(error),
    });
  });

  function confirmApply(reason: string) {
    if (!preview) return;
    apply.mutate(
      { settings: preview.settings, reason, base_version: preview.result.base_version, trigger },
      {
        onSuccess: (version) => {
          setPreview(null);
          setTrigger('manual');
          reset(cloneSettings(version.settings));
          if (version.delivery === 'delivered') {
            toast.mostrar(t('sites.admission.toast.applied', { version: version.version }), { tone: 'sucesso' });
          } else {
            toast.mostrar(t('sites.admission.toast.savedNotApplied', { version: version.version, code: version.delivery_code ?? '—' }), { tone: 'aviso' });
          }
        },
        onError: (error) => {
          if (serverFields(error)) setPreview(null);
        },
      },
    );
  }

  function restore(version: AdmissionVersion) {
    reset(saved);
    for (const [key, value] of Object.entries(cloneSettings(version.settings))) {
      setValue(key as keyof AdmissionSettings, value as never, { shouldDirty: true, shouldValidate: true });
    }
    setTrigger('restore');
    setHistory(false);
    toast.mostrar(t('sites.admission.toast.restored', { version: version.version }), { tone: 'sucesso' });
  }

  const latest = page.latest;
  const failed = latest?.delivery === 'failed';

  return (
    <div className={s.stack}>
      <Status page={page} onHistory={() => setHistory(true)} onRefresh={() => refresh.mutate()} refreshing={refresh.isPending} />
      {refresh.error ? <ErrorBanner error={refresh.error} title={t('sites.admission.error.refresh')} hideLink /> : null}

      {failed ? (
        <Alert tone="danger" title={t('sites.admission.failed.title', { version: latest.version })}>
          {t('sites.admission.failed.message', { code: latest.delivery_code ?? '—' })}{' '}
          <LoadingButton variant="out" loading={retry.isPending} loadingLabel={t('sites.admission.failed.retrying')} onClick={() => retry.mutate()}>
            {t('sites.admission.failed.retry')}
          </LoadingButton>
        </Alert>
      ) : null}
      {retry.error ? <ErrorBanner error={retry.error} title={t('sites.admission.failed.retryError')} hideLink /> : null}
      {page.observed.proxy_runs_as_root ? (
        <Alert tone="warning" title={t('sites.admission.root.title')}>
          {t('sites.admission.root.message')}
        </Alert>
      ) : null}

      <Card title={t('sites.admission.registration.title')}>
        {reregister ? (
          <AdmissionRegistration
            siteId={siteId}
            registration={page.registration}
            mode="again"
            onCancel={() => setReregister(false)}
            onRegistered={() => {
              setReregister(false);
              toast.mostrar(t('sites.admission.toast.registered'), { tone: 'sucesso' });
            }}
          />
        ) : (
          <>
            <dl className={s.facts}>
              <dt>{t('sites.admission.registration.endpoint')}</dt>
              <dd className={s.mono}>{page.registration.endpoint}</dd>
              <dt>{t('sites.admission.registration.registeredAt')}</dt>
              <dd>
                {dataHour(page.registration.registered_at) ?? '—'} ·{' '}
                {t('sites.admission.registration.certificate')}{' '}
                <span className={s.mono} title={page.registration.certificate_sha256}>
                  {shortFingerprint(page.registration.certificate_sha256)}
                </span>
              </dd>
            </dl>
            <div className={s.row}>
              <Button variant="out" onClick={() => setReregister(true)}>
                {t('sites.admission.registration.again')}
              </Button>
            </div>
          </>
        )}
      </Card>

      <Card title={t('sites.admission.identity.title')}>
        <dl className={s.facts}>
          <dt>{t('sites.admission.identity.template')}</dt>
          <dd className={s.mono}>{page.identity.spiffe_template}</dd>
          <dt>{t('sites.admission.identity.trustDomain')}</dt>
          <dd className={s.mono}>{page.identity.trust_domain || t('sites.admission.identity.trustDomain.none')}</dd>
        </dl>
        <div className={s.row}>
          <span className={s.note}>{t('sites.admission.identity.note')}</span>
          <Link to={`/sites?drawer=${page.site_id}`}>{t('sites.admission.identity.edit')}</Link>
        </div>
      </Card>

      <form onSubmit={review} noValidate className={s.stack}>
        <AdmissionSettingsForm control={control} setValue={setValue} page={page} />

        <Card title={t('sites.admission.releases.title')}>
          <p data-testid="admission-release-count">
            {tn('sites.admission.releases.count', page.release_count, { n: page.release_count })}{' '}
            <Link to="/aprovacoes">{t('sites.admission.releases.link')}</Link>
          </p>
        </Card>

        {previewing.error && previewing.error.campos.length === 0 ? (
          <ErrorBanner error={previewing.error} title={t('sites.admission.error.preview')} hideLink />
        ) : null}
        <div className={s.footer}>
          <Button
            variant="out"
            disabled={!formState.isDirty}
            onClick={() => {
              reset(saved);
              setTrigger('manual');
            }}
          >
            {t('sites.admission.discard')}
          </Button>
          <LoadingButton type="submit" loading={previewing.isPending} loadingLabel={t('sites.admission.reviewing')}>
            {t('sites.admission.review')}
          </LoadingButton>
        </div>
      </form>

      {preview ? (
        <AdmissionApplyDialog
          siteName={page.site_name}
          preview={preview.result}
          pending={apply.isPending}
          error={apply.error && apply.error.campos.length === 0 ? apply.error : null}
          onApply={confirmApply}
          onCancel={() => {
            setPreview(null);
            apply.reset();
          }}
        />
      ) : null}
      {history ? <AdmissionHistoryDialog siteId={siteId} siteName={page.site_name} onRestore={restore} onClose={() => setHistory(false)} /> : null}
    </div>
  );
}

/** A linha de estado do protótipo §2: o chip, quem salvou, quando, e quando o cluster confirmou. */
function Status({
  page,
  onHistory,
  onRefresh,
  refreshing = false,
}: {
  page: SiteAdmission;
  onHistory?: () => void;
  onRefresh?: () => void;
  refreshing?: boolean;
}) {
  const latest = page.latest;
  return (
    <div className={s.status}>
      <AdmissionChip summary={page.summary} />
      {latest ? (
        <span className={s.statusText}>
          {t('sites.admission.status.saved', { who: latest.created_by, when: dataHour(latest.created_at) ?? '—' })}
          {latest.delivered_at ? ` · ${t('sites.admission.status.confirmed', { when: dataHour(latest.delivered_at) ?? '—' })}` : ''}
        </span>
      ) : null}
      <span className={s.statusActions}>
        {onRefresh ? (
          <LoadingButton variant="soft" icon="refresh" loading={refreshing} loadingLabel={t('sites.admission.status.refreshing')} onClick={onRefresh}>
            {t('sites.admission.status.refresh')}
          </LoadingButton>
        ) : null}
        {onHistory && latest ? (
          <Button variant="out" icon="clock" onClick={onHistory}>
            {t('sites.admission.status.history')}
          </Button>
        ) : null}
      </span>
    </div>
  );
}
